/* eslint-disable no-magic-numbers */
/* eslint-disable @typescript-eslint/no-explicit-any */

import { DateCell, Tooltip, Typography } from '@omnipresentgroup/design-system';
import { useXeroConnection } from '@omnipresentgroup/domains';
import { toSentenceCase } from '@omnipresentgroup/helpers';
import { useFlags } from '@omnipresentgroup/launchdarkly';
import {
  type InvoiceReport,
  type InvoiceReportType,
  XeroConnectionStatusEnum,
} from '@omnipresentgroup/types';
import { type ColumnDef, createColumnHelper } from '@tanstack/react-table';
import { useMemo } from 'react';

import { useManagerUser } from '../../../hooks';
import { AmountCell, DueDateCell, XeroStatusCell } from '../components';
import { InvoiceReportPaymentStatus } from '../InvoiceReportPaymentStatus';
import { InvoiceReportRowActions } from '../InvoiceReportRowActions';
import { InvoiceReportStatus } from '../InvoiceReportStatus';

export enum ColumnHeaders {
  invoiceId = 'Invoice',
  reference = 'Memo / Pay period',
  issueDate = 'Issue date',
  dueDate = 'Due date',
  amountCell = 'Amount',
  paymentStatus = 'Payment status',
  approvalStatus = 'Approval status',
  xeroStatus = 'Xero sync',
  action = '',
  type = 'Type',
}

export const useInvoiceReportsTableConfig = () => {
  const { enableInvoiceBreakdown } = useFlags();
  const { companyId } = useManagerUser();

  const { data: xeroConnectionStatus } = useXeroConnection({
    clientId: companyId,
    enabled: !!companyId,
    throwOnError: false,
  });

  const columnHelper = useMemo(() => createColumnHelper<InvoiceReport>(), []);

  const columns: ColumnDef<InvoiceReport, any>[] = useMemo(
    () => [
      columnHelper.accessor('id', {
        id: 'id',
        cell: (info) => info.getValue() ?? '',
        header: () => ColumnHeaders.invoiceId,
        size: 75,
      }),
      columnHelper.accessor('type', {
        cell: (info) => toSentenceCase(info.getValue() ?? ''),
        header: () => ColumnHeaders.type,
      }),
      columnHelper.accessor('reference', {
        cell: (info) => {
          const reference = info.getValue() ?? '';
          return reference.length >= 30 ? (
            <Tooltip
              id="show-invoice-memo"
              description={reference}
              align="start"
            >
              <Typography as="span" size="14">
                {`${reference.slice(0, 30)}...`}
              </Typography>
            </Tooltip>
          ) : (
            reference
          );
        },
        header: () => ColumnHeaders.reference,
      }),
      columnHelper.accessor('issueDate', {
        cell: (info) => <DateCell value={info.getValue()} />,
        header: () => ColumnHeaders.issueDate,
      }),
      columnHelper.accessor('dueDate', {
        cell: (info) => {
          const dueDate = info.row.original.dueDate;
          const status = info.row.original.status;
          const invoiceType = info.row.original
            .invoiceType as InvoiceReportType;
          const invoiceTransactionType = info.row.original.type;
          return (
            <DueDateCell
              dueDate={dueDate}
              status={status}
              invoiceType={invoiceType}
              invoiceTransactionType={invoiceTransactionType}
            />
          );
        },
        header: () => ColumnHeaders.dueDate,
      }),
      columnHelper.accessor('totalAmount', {
        cell: (info) => {
          const amount = Number(info.row.original.totalAmount.amount);
          const type = info.row.original.type;
          const currency = info.row.original.totalAmount.currency;
          const parentTransactionId = info.row.original.parentTransactionId;
          return (
            <AmountCell
              type={type}
              amount={amount}
              currency={currency}
              parentTransactionId={parentTransactionId}
            />
          );
        },
        header: () => ColumnHeaders.amountCell,
      }),
      columnHelper.display({
        id: 'paymentStatus',
        cell: (info) => {
          const status = info.row.original.status;
          const dueDate = info.row.original.dueDate;
          const type = info.row.original.type;
          return (
            <InvoiceReportPaymentStatus
              status={status}
              dueDate={dueDate}
              type={type}
            />
          );
        },
        header: () => ColumnHeaders.paymentStatus,
      }),
      ...(enableInvoiceBreakdown
        ? [
            columnHelper.display({
              id: 'approvalStatus',
              cell: (info) => (
                <InvoiceReportStatus approved={info.row.original.approved} />
              ),
              header: () => ColumnHeaders.approvalStatus,
            }),
          ]
        : []),

      ...(xeroConnectionStatus?.connectionStatus ===
      XeroConnectionStatusEnum.CONNECTED
        ? [
            columnHelper.display({
              id: 'xeroSync',
              cell: (info) => (
                <XeroStatusCell syncStatuses={info.row.original.syncStatuses} />
              ),
              header: () => ColumnHeaders.xeroStatus,
              meta: {
                onClickStopPropagation: true,
              },
            }),
          ]
        : []),
      columnHelper.display({
        id: 'action',
        cell: (info) => <InvoiceReportRowActions invoice={info.row.original} />,
        header: () => ColumnHeaders.action,
        meta: {
          onClickStopPropagation: true,
        },
      }),
    ],
    [
      columnHelper,
      enableInvoiceBreakdown,
      xeroConnectionStatus?.connectionStatus,
    ],
  );

  return {
    columns,
  };
};
